import { Directive, EventEmitter, inject, Input, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalService, ModalSettings, WizardMarkableActions } from 'mbs-ui-kit';
import { MbsPopupType } from 'mbs-ui-kit/utils/enums/mbs-popup-enum';
import { FormsUtil } from 'mbs-ui-kit/utils/forms-util';
import { AzureAccessTierType, StorageClass, StorageType } from 'mbs-ui-shared@models';
import { PlanMode, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { BehaviorSubject, OperatorFunction } from 'rxjs';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { DataForPath } from '../helpers/bases/base-for-steps-helper';
import { RemoteManagementWizardsService } from '../services/remote-management-wizards.service';
import { EnableStepModalComponent } from './components/enable-step-modal/enable-step-modal.component';
import { LegacyPropertyDisablingModalComponent } from './components/legacy-property-disabling-modal.component';

export enum AgentVersions {
  AzureCold = '8.0.0',
  AzureColdLinux = '4.4',
  BlockCount = '7.1.1',
  EFS = '7.6',
  FFI = '7.8',
  FfiSyntheticFull = '8.8.5',
  GlacierIR = '7.3.1',
  GlacierIRLinux = '3.3.1',
  UnixPathVariables = '4.6'
}

export type StepLoadInfo = { loading: boolean; isHyperV?: boolean };

export type FormPipeOperators = [
  OperatorFunction<unknown, unknown>?,
  OperatorFunction<unknown, unknown>?,
  OperatorFunction<unknown, unknown>?,
  OperatorFunction<unknown, unknown>?
];

export enum AfterEnabledStepActionType {
  next = 0,
  back = 1,
  save = 2,
  custom = 3
}

const ProxmoxSyntheticFullVersion = 865;

const IBBEnableOtherHidden: { [planType: string]: 'Enabled' | 'Disabled' | 'Hidden' } = {
  [PlanTypes.BackupDiskImagePlan]: 'Enabled',
  [PlanTypes.Plan]: 'Hidden',
  [PlanTypes.DataBasePlan]: 'Hidden',
  [PlanTypes.ExchangePlan]: 'Hidden',
  [PlanTypes.BackupVMware]: 'Hidden',
  [PlanTypes.BackupHyperV]: 'Hidden'
};

@UntilDestroy()
@Directive()
export abstract class StepBase<T extends { valid?: boolean }> implements ControlValueAccessor {
  @Input() title: string;
  @Input() planTypeText = 'Default';
  @Input() stepFocused = false;
  @Input() helpLink: string;

  @Output() loadInfo = new EventEmitter<StepLoadInfo>();
  @Output() isValid = new EventEmitter<boolean>();
  @Output() registerRequiredStep = new EventEmitter<string>();
  @Output() unregisterRequiredStep = new EventEmitter<string>();
  @Output() nextStep = new EventEmitter<boolean>();
  @Output() backStep = new EventEmitter();
  @Output() selectStep = new EventEmitter<string>();
  @Output() addStepMark = new EventEmitter<{ stepName: string; actions: WizardMarkableActions }>();
  @Output() removeStepMark = new EventEmitter<{ stepName: string; actions: Array<keyof WizardMarkableActions> }>();

  public readonly mbsPopupType = MbsPopupType;
  public readonly s3CompatibleStorages = [StorageType.S3Compatible, StorageType.Minio, StorageType.IDriveE2, StorageType.ImpossibleCloud];

  public syntheticFullVisible: 'Enabled' | 'Disabled' | 'Hidden' = null;
  public importedPlanChanged = false;
  public stepForm: UntypedFormGroup;
  public regExpForInputAfter = /\\[ ]/;
  public regExpForInputBefore = /[ ]\\/;
  public splitForInputBefore = ' \\';
  public splitForInput = '\\ ';
  public joinForInput = '\\';
  public selectedStorageType: StorageType = null;
  public userInteractWithSteps: boolean;

  onChange: (value: unknown) => void;
  onTouched: () => void;

  protected initialSyntheticFull: boolean;
  protected confirmedAsEnabled = false;
  protected legacyUnsupportedProperties: Array<string> = [];
  protected syntheticFullSettingPossible = new BehaviorSubject<boolean>(null);
  /**
   * [StorageType]-[StorageClass | AzureAccessTierType | null]-[storage allow synthetic | null]-[isNBF]: 'Enabled' | 'Disabled' | 'Hidden'
   * [StorageType]-[StorageClass | AzureAccessTierType | null]-[storage allow synthetic | null]-[isNBF]: { [PlanTypes]: 'Enabled' | 'Disabled' | 'Hidden' }
   * https://docs.google.com/spreadsheets/d/1D0XRduqatjPsGXFKKk92bOZ9Pq9wgTzQmo_1oHCOQ-c/
   * @private
   */
  protected syntheticFullVisibleMap: {
    windows: { [key: string]: 'Enabled' | 'Disabled' | 'Hidden' | { [planType: string]: 'Enabled' | 'Disabled' | 'Hidden' } };
    unix: { [key: string]: 'Enabled' | 'Disabled' | 'Hidden' | { [planType: string]: 'Enabled' | 'Disabled' | 'Hidden' } };
  } = {
    windows: {
      [`${StorageType.Azure}-${AzureAccessTierType.Hot}-${null}-${true}`]: 'Enabled',
      [`${StorageType.Azure}-${AzureAccessTierType.Cool}-${null}-${true}`]: 'Enabled',
      [`${StorageType.Azure}-${AzureAccessTierType.Cold}-${null}-${true}`]: 'Disabled',
      [`${StorageType.Azure}-${AzureAccessTierType.Archive}-${null}-${true}`]: 'Disabled',
      [`${StorageType.Azure}-${AzureAccessTierType.Hot}-${null}-${false}`]: 'Hidden',
      [`${StorageType.Azure}-${AzureAccessTierType.Cool}-${null}-${false}`]: 'Hidden',
      [`${StorageType.Azure}-${AzureAccessTierType.Cold}-${null}-${false}`]: 'Hidden',
      [`${StorageType.Azure}-${AzureAccessTierType.Archive}-${null}-${false}`]: 'Hidden',
      [`${StorageType.BackblazeB2}-${null}-${null}-${true}`]: 'Enabled',
      [`${StorageType.BackblazeB2}-${null}-${null}-${false}`]: 'Hidden',
      [`${StorageType.GoogleCloud}-${null}-${null}-${true}`]: 'Hidden',
      [`${StorageType.GoogleCloud}-${null}-${null}-${false}`]: 'Hidden',
      [`${StorageType.Minio}-${null}-${true}-${true}`]: 'Enabled',
      [`${StorageType.Minio}-${null}-${false}-${true}`]: 'Hidden',
      [`${StorageType.Minio}-${null}-${false}-${false}`]: 'Hidden',
      [`${StorageType.Minio}-${null}-${true}-${false}`]: IBBEnableOtherHidden,

      [`${StorageType.AmazonS3}-${StorageClass.STANDARD}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3}-${StorageClass.INTELLIGENT_TIERING}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3}-${StorageClass.STANDARD_IA}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3}-${StorageClass.ONEZONE_IA}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3}-${StorageClass.STANDARD}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.INTELLIGENT_TIERING}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.STANDARD_IA}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.ONEZONE_IA}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3}-${StorageClass.GLACIER}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3}-${StorageClass.GLACIER}-${null}-${false}`]: 'Hidden',
      [`${StorageType.AmazonS3}-${StorageClass.GLACIER_IR}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3}-${StorageClass.GLACIER_IR}-${null}-${false}`]: 'Hidden',
      [`${StorageType.AmazonS3}-${StorageClass.DEEP_ARCHIVE}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3}-${StorageClass.DEEP_ARCHIVE}-${null}-${false}`]: 'Hidden',

      [`${StorageType.AmazonS3Eu}-${StorageClass.STANDARD}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3Eu}-${StorageClass.INTELLIGENT_TIERING}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3Eu}-${StorageClass.STANDARD_IA}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3Eu}-${StorageClass.ONEZONE_IA}-${null}-${true}`]: 'Enabled',
      [`${StorageType.AmazonS3Eu}-${StorageClass.STANDARD}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3Eu}-${StorageClass.INTELLIGENT_TIERING}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3Eu}-${StorageClass.STANDARD_IA}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3Eu}-${StorageClass.ONEZONE_IA}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.AmazonS3Eu}-${StorageClass.GLACIER}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3Eu}-${StorageClass.GLACIER}-${null}-${false}`]: 'Hidden',
      [`${StorageType.AmazonS3Eu}-${StorageClass.GLACIER_IR}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3Eu}-${StorageClass.GLACIER_IR}-${null}-${false}`]: 'Hidden',
      [`${StorageType.AmazonS3Eu}-${StorageClass.DEEP_ARCHIVE}-${null}-${true}`]: 'Disabled',
      [`${StorageType.AmazonS3Eu}-${StorageClass.DEEP_ARCHIVE}-${null}-${false}`]: 'Hidden',

      [`${StorageType.UbAmazonS3}-${null}-${null}-${true}`]: 'Enabled',
      [`${StorageType.UbAmazonS3}-${null}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.S3Compatible}-${null}-${true}-${true}`]: 'Enabled',
      [`${StorageType.S3Compatible}-${null}-${false}-${true}`]: 'Hidden',
      [`${StorageType.S3Compatible}-${null}-${false}-${false}`]: 'Hidden',
      [`${StorageType.S3Compatible}-${null}-${true}-${false}`]: IBBEnableOtherHidden,

      [`${StorageType.ImpossibleCloud}-${null}-${true}-${true}`]: 'Enabled',
      [`${StorageType.ImpossibleCloud}-${null}-${false}-${true}`]: 'Hidden',
      [`${StorageType.ImpossibleCloud}-${null}-${false}-${false}`]: 'Hidden',
      [`${StorageType.ImpossibleCloud}-${null}-${true}-${false}`]: IBBEnableOtherHidden,

      [`${StorageType.IDriveE2}-${null}-${true}-${true}`]: 'Enabled',
      [`${StorageType.IDriveE2}-${null}-${false}-${true}`]: 'Hidden',
      [`${StorageType.IDriveE2}-${null}-${false}-${false}`]: 'Hidden',
      [`${StorageType.IDriveE2}-${null}-${true}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.Wasabi}-${null}-${null}-${true}`]: 'Enabled',
      [`${StorageType.Wasabi}-${null}-${null}-${false}`]: IBBEnableOtherHidden,
      [`${StorageType.CblWasabi}-${null}-${null}-${true}`]: 'Enabled',
      [`${StorageType.CblWasabi}-${null}-${null}-${false}`]: IBBEnableOtherHidden,
      Other: 'Hidden'
    },
    unix: {
      All: 'Hidden'
    }
  };

  private myValue: T = null;

  set value(value: T) {
    this.myValue = value;
    this.notifyValueChange();
  }

  get value(): T {
    return this.myValue;
  }

  get planType(): PlanTypes {
    return this.mainService.type;
  }

  get isNBF(): boolean {
    return this.mainService.isNBF;
  }

  get isOffline(): boolean {
    return !this.isRDMode && this.mainService.isOffline;
  }

  get isLinux(): boolean {
    return this.mainService.isLinux;
  }

  get isMac(): boolean {
    return this.mainService.isMac;
  }

  get isUnix(): boolean {
    return this.mainService.isUnix;
  }

  get isNetBuild(): boolean {
    return this.mainService.isNetBuild;
  }

  get isRDMode(): boolean {
    return this.state.isRDMode;
  }

  get isCreate(): boolean {
    return this.mainService.mode === PlanMode.create;
  }

  get isEdit(): boolean {
    return this.mainService.mode === PlanMode.edit;
  }

  get isConsistencyCheck(): boolean {
    return this.planType === PlanTypes.ConsistencyCheck;
  }

  get isHyperV(): boolean {
    return this.state.isHyperV;
  }

  get isProxmox(): boolean {
    return this.planType === PlanTypes.BackupProxmox;
  }

  get isVMWare(): boolean {
    return this.state.isVMWare;
  }

  get isRestore(): boolean {
    return this.state.isRestore;
  }

  get isRestoreDB(): boolean {
    return this.isRestoreFileDatabase || this.isRestoreDatabase;
  }

  get isRestoreFileDatabase(): boolean {
    return this.planType === PlanTypes.RestoreDatabaseFilesPlan;
  }

  get isRestoreDatabase(): boolean {
    return this.planType === PlanTypes.RestoreDataBasePlan;
  }

  get isRestorePlan(): boolean {
    return this.planType === PlanTypes.RestorePlan;
  }

  get isRestoreIbb(): boolean {
    return this.planType === PlanTypes.RestoreDiskImagePlan;
  }

  get isRestoreVMWareToVSphere(): boolean {
    return this.planType === PlanTypes.RestoreVMware;
  }

  get isRestoreVMWare(): boolean {
    return this.isRestoreVMWareToVSphere || this.planType === PlanTypes.RestoreVMwareToCustom;
  }

  get isRestoreProxmox(): boolean {
    return this.planType === PlanTypes.RestoreProxmox;
  }

  get isRestoreVMWareOrProxmox(): boolean {
    return this.isRestoreVMWare || this.isRestoreProxmox;
  }

  get isRestoreHyperV(): boolean {
    return this.planType === PlanTypes.RestoreHyperV || this.planType === PlanTypes.RestoreHyperVToCustom;
  }

  get isVMRestore(): boolean {
    return this.isRestoreVMWare || this.isRestoreHyperV || this.isRestoreProxmox;
  }

  get isIBBPlan(): boolean {
    return this.planType === PlanTypes.BackupDiskImagePlan;
  }

  get isBackupPlan(): boolean {
    return this.planType === PlanTypes.Plan;
  }

  get isSQLPlan(): boolean {
    return this.state.isSQLPlan;
  }

  get canShowFFI(): boolean {
    if ((this.isUnix && !this.isNetBuild) || this.isFFIEnabled || this.isRestore || !this.isNBF) {
      return false;
    }

    return true;
  }

  get isFFIEnabled(): boolean {
    if ((this.isUnix && !this.isNetBuild) || this.isRestore || this.isSQLPlan) {
      return false;
    }

    const rightVersion = this.agentHigherOrEqualThat(AgentVersions.FFI);
    return (this.isRDMode && this.isNBF) || (this.isNBF && rightVersion);
  }

  get isFfiSyntheticFullEnabled(): boolean {
    return !this.isRDMode && this.agentHigherOrEqualThat(AgentVersions.FfiSyntheticFull);
  }

  get canFFIAndSyntheticForLocal(): boolean {
    return this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 3) >= 875;
  }

  get canSyntheticForProxmox(): boolean {
    return this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 3) >= ProxmoxSyntheticFullVersion;
  }

  get isAppAwareEnabled(): boolean {
    return this.state.isAppAwareEnabled;
  }

  private get state() {
    return RemoteManagementWizardsService.getWizardState(this.mainService);
  }

  public mainService = inject(RemoteManagementWizardsService);

  protected constructor() {}

  /**
   * Method for mass change state
   * Also can consider legacyUnsupportedProperties property
   * @param {String[]} controlNames
   * @param {Boolean} isEnabled
   * @param {FormGroup} stepForm
   * @protected
   */
  public toggleFormControls(controlNames: string[], isEnabled: boolean, stepForm?: FormGroup): void {
    let controls = controlNames;
    const actionName = isEnabled ? 'enable' : 'disable';

    if (isEnabled && !this.isNBF) {
      controls = controls.filter((name) => !this.legacyUnsupportedProperties.includes(name));
    }

    controls.forEach((name: string) => (stepForm || this.stepForm).get(name)?.[actionName]?.());
  }

  abstract initForm(): void;

  public agentHigherThat(version: AgentVersions): boolean {
    const v = version.replace(/[.]/g, '');

    return this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, v.length) > +v;
  }

  public agentHigherOrEqualThat(version: AgentVersions): boolean {
    const v = version.replace(/[.]/g, '');

    return this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, v.length) >= +v;
  }

  /**
   * Don`t forget to use <mbs-wizards-legacy-property-tooltip *ngIf="stepForm.get(...).disabled"></mbs-wizards-legacy-property-tooltip>
   * @param {Boolean} force
   * @protected
   * @see legacyUnsupportedProperties
   * @see getLegacyPropertyChangeHandler
   */
  protected disableLegacyUnsupportedProperties(force = false): void {
    if (this.isNBF) {
      return;
    }

    this.legacyUnsupportedProperties.forEach((key) => {
      if (!this.stepForm.get(key).value || force) {
        this.stepForm.get(key).disable();
      }
    });
  }

  protected getPipeOperators(): FormPipeOperators {
    return [untilDestroyed(this)];
  }

  protected initFormEvents(stepForm?: UntypedFormGroup): void {
    (stepForm || this.stepForm).valueChanges.pipe(...this.getPipeOperators()).subscribe({
      next: (value: T) => {
        this.onStepFormChange(value, stepForm);

        if (this.stepForm.touched && this.stepForm.dirty && this.onTouched) {
          this.onTouched();
        }
      }
    });
  }

  onStepFormChange(value: T, stepForm?: UntypedFormGroup): void {
    this.value = { ...value, valid: (stepForm || this.stepForm).valid };
  }

  updateForm(value: T): void {
    this.stepForm.reset(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  forceValid(data: ForceValidType = null): void {
    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  resetValidStateForValidFields(): void {
    if (this.stepForm) {
      const value = this.stepForm.getRawValue();
      for (const key in value) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) continue;

        const ctrl = this.stepForm.get(key) as FormControl;
        if (ctrl.valid) ctrl.reset(value[key]);
      }
    }
  }

  setValues(): void {
    if (this.isUnix) {
      this.regExpForInputAfter = /\/[ ]/;
      this.regExpForInputBefore = /[ ]\//;
      this.splitForInput = '/ ';
      this.splitForInputBefore = ' /';
      this.joinForInput = '/';
    }
  }

  getValueForOperationsWithPath(getWin = false): DataForPath {
    if (getWin) {
      return {
        isUnix: false,
        regExpAfter: /\\[ ]/,
        regExpBefore: /[ ]\\/,
        split: '\\ ',
        join: '\\',
        splitForBefore: ' \\'
      };
    }

    return {
      isUnix: this.isUnix,
      regExpAfter: this.regExpForInputAfter,
      regExpBefore: this.regExpForInputBefore,
      split: this.splitForInput,
      join: this.joinForInput,
      splitForBefore: this.splitForInputBefore
    };
  }

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  writeValue(value: T): void {
    this.myValue = value;
    if (value) this.updateForm(value);
  }

  registerOnChange(fn: (data: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected showEnableStepModal(
    modalService: ModalService,
    settings: ModalSettings = { collapsing: true },
    type: AfterEnabledStepActionType = AfterEnabledStepActionType.next
  ): Promise<boolean> {
    const isSave = type === AfterEnabledStepActionType.save;
    const actionName = type === AfterEnabledStepActionType.back ? 'backStep' : 'nextStep';
    const promise = modalService.openCustom<boolean>(EnableStepModalComponent, settings);

    if (type !== AfterEnabledStepActionType.custom) {
      promise
        .then((confirm) => {
          if (!confirm) return void (isSave ? this[actionName].emit(isSave) : this[actionName].emit());

          this.confirmedAsEnabled = true;

          return confirm;
        })
        .catch(() => (isSave ? this[actionName].emit(isSave) : this[actionName].emit()));
    }

    return promise;
  }

  getLegacyPropertyChangeHandler(propertyName: string, propertyTitle: string, modalService: ModalService): (newValue: boolean) => void {
    return (newValue: boolean) => {
      if (!this.isNBF && !newValue) {
        this.askDisablingLegacyProperty(propertyTitle, modalService).then(
          (confirmDisabling) => confirmDisabling && this.stepForm.get(propertyName).setValue(false)
        );

        this.stepForm.get(propertyName).setValue(true);
      }
    };
  }

  protected askDisablingLegacyProperty(propertyTitle: string, modalService: ModalService): Promise<boolean> {
    return modalService
      .openCustom(LegacyPropertyDisablingModalComponent, { collapsing: true, data: { propertyTitle } })
      .then(() => true)
      .catch(() => false);
  }

  public removeError(controls: AbstractControl[], error: string): void {
    controls.forEach((control) => {
      const errors = control.errors || {};

      delete errors[error];

      control.setErrors(Object.keys(errors).length ? errors : null);
    });
  }

  public recalculateSyntheticFullVisible(
    storageType: StorageType,
    fStorageClass: StorageClass,
    fAzureAccessTierType: AzureAccessTierType,
    syntheticFullField: string
  ): void {
    if ((this.isProxmox && !this.canSyntheticForProxmox) || (!this.selectedStorageType && this.isUnix && !this.isNetBuild)) {
      return void this.setSyntheticFullVisible('Hidden', syntheticFullField);
    }

    /**
     * Need to combine string [StorageType]-[StorageClass | AzureAccessTierType | null]-[storage allow synthetic | null]-[isNBF]
     */
    let allowSynthetic: boolean = null;
    let storageClass: StorageClass | AzureAccessTierType = null;

    if (storageType === StorageType.Azure) {
      storageClass = fAzureAccessTierType;
    }

    if (storageType === StorageType.AmazonS3 || storageType === StorageType.AmazonS3Eu) {
      storageClass = fStorageClass;
    }

    if (this.s3CompatibleStorages.includes(storageType)) {
      allowSynthetic = this.syntheticFullSettingPossible.value;
    }

    const selector = `${storageType}-${storageClass}-${allowSynthetic}-${this.isNBF}`;
    let value = this.syntheticFullVisibleMap.windows[selector] || 'Hidden';

    if (typeof value === 'object') {
      value = value[this.mainService.type] || 'Hidden';
    }

    if (storageType === StorageType.LocalFS && this.canFFIAndSyntheticForLocal) {
      value = 'Enabled';
    }

    this.setSyntheticFullVisible(value, syntheticFullField);
  }

  protected setSyntheticFullVisible(value: 'Enabled' | 'Disabled' | 'Hidden', syntheticFullField: string): void {
    const setTrue = () => {
      if (!this.userInteractWithSteps && !this.initialSyntheticFull) {
        return;
      }

      this.syntheticFullForm().get(syntheticFullField).setValue(true);
    };

    this.syntheticFullVisible = value;

    if (value === 'Enabled') {
      return void setTrue();
    }

    this.syntheticFullForm().get(syntheticFullField).setValue(false);
  }

  protected syntheticFullForm(): FormGroup {
    return this.stepForm;
  }
}
